import type { JSX } from 'react';
import { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { rememberReturnPath, takeReturnPath } from '../lib/returnToPath';
import { useLoginModalStore } from '../stores/loginModalStore';

/**
 * PD-16863 — a `<Navigate replace>` that first remembers the page being left,
 * so the visitor comes back to it once they are signed in and have an account
 * selected (see `useReturnAfterSignIn`). Used where the storefront sends a
 * visitor away from a page they followed a link to: to sign in, or to pick an
 * account.
 *
 * `openSignIn` also opens the sign-in form, for a guest-browsing storefront
 * where the redirect lands on a page a guest can see, so nothing else would
 * prompt them to sign in.
 */
export function NavigateRememberingReturn({
	to,
	openSignIn = false,
}: {
	to: string;
	openSignIn?: boolean;
}): JSX.Element {
	const location = useLocation();
	const from = `${location.pathname}${location.search}${location.hash}`;
	const openLoginModal = useLoginModalStore((s) => s.open);

	// The `from` captured at render is the page being left, even though the
	// child <Navigate> has already moved the router on when this runs.
	useEffect(() => {
		rememberReturnPath(from);
		if (openSignIn) {
			openLoginModal();
		}
	}, [from, openSignIn, openLoginModal]);

	return <Navigate to={to} replace />;
}

/**
 * PD-16863 — once `ready` (signed in, an account selected, session restored),
 * go to the page remembered by `NavigateRememberingReturn`, if there is one.
 * The path is read once and cleared, so this fires on the transition to
 * ready, not on every later navigation. Mounted in PortalLayout, which every
 * signed-in page renders inside, so it covers signing in from Home, from the
 * sign-in form over any page, and coming back from the account picker.
 */
export function useReturnAfterSignIn(ready: boolean): void {
	const navigate = useNavigate();
	const location = useLocation();
	const here = `${location.pathname}${location.search}${location.hash}`;

	useEffect(() => {
		if (!ready) {
			return;
		}
		const target = takeReturnPath();
		if (target != null && target !== here) {
			void navigate(target, { replace: true });
		}
		// Only the transition to ready matters; `here` and `navigate` are read
		// at that moment, and the remembered path is one-shot.
	}, [ready]);
}
